import { TimeReportExcelRow } from '@app/shared/types/models/reports/time-report-excel-row.model';
import { ReportColumnsEnum } from '@app/shared/types/models/reports/time-report-panel.model';
import * as XLSX from 'xlsx';
import { excelCellValue, TimeReportExcelFormatContext } from './time-report-excel.format';

export const DEFAULT_REPORT_EXCEL_COLUMNS: ReportColumnsEnum[] = [
  ReportColumnsEnum.DATE,
  ReportColumnsEnum.MONTH,
  ReportColumnsEnum.WEEK,
  ReportColumnsEnum.USER,
  ReportColumnsEnum.COMPANY,
  ReportColumnsEnum.PROJECT,
  ReportColumnsEnum.TASK_ID,
  ReportColumnsEnum.TASK_LINK,
  ReportColumnsEnum.TASK_TITLE,
  ReportColumnsEnum.LOCATION,
  ReportColumnsEnum.DESCRIPTION,
  ReportColumnsEnum.TYPE,
  ReportColumnsEnum.TOTAL,
  ReportColumnsEnum.DECIMAL_HOURS,
  ReportColumnsEnum.ESTIMATION,
  ReportColumnsEnum.STATUS
];

const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  [ReportColumnsEnum.DATE]: 15,
  [ReportColumnsEnum.MONTH]: 15,
  [ReportColumnsEnum.WEEK]: 15,
  [ReportColumnsEnum.USER]: 25,
  [ReportColumnsEnum.COMPANY]: 25,
  [ReportColumnsEnum.PROJECT]: 30,
  [ReportColumnsEnum.TASK_ID]: 20,
  [ReportColumnsEnum.TASK_LINK]: 20,
  [ReportColumnsEnum.TASK_TITLE]: 40,
  [ReportColumnsEnum.LOCATION]: 30,
  [ReportColumnsEnum.DESCRIPTION]: 40,
  [ReportColumnsEnum.TYPE]: 15,
  [ReportColumnsEnum.TOTAL]: 15,
  [ReportColumnsEnum.DECIMAL_HOURS]: 15,
  [ReportColumnsEnum.STATUS]: 15,
  [ReportColumnsEnum.STANDARD]: 15,
  [ReportColumnsEnum.BANK_PLUS]: 15,
  [ReportColumnsEnum.BANK_MINUS]: 15,
  [ReportColumnsEnum.OVERTIME_50]: 15,
  [ReportColumnsEnum.OVERTIME_100]: 15,
  [ReportColumnsEnum.ESTIMATION]: 15
};

export interface BuildTimeReportExcelWorkbookParams {
  excelRows: TimeReportExcelRow[];
  columns: ReportColumnsEnum[];
  columnLabel: (column: ReportColumnsEnum) => string;
  formatContext: TimeReportExcelFormatContext;
}

export function buildTimeReportExcelWorkbook(params: BuildTimeReportExcelWorkbookParams): XLSX.WorkBook {
  const { excelRows, columns, columnLabel, formatContext } = params;

  const mappedData = excelRows.map(row => {
    const object: Record<string, unknown> = {};
    columns.forEach(column => {
      object[columnLabel(column)] = excelCellValue(row, column, formatContext);
    });
    return object;
  });

  const sortedData = mappedData.map(row =>
    Object.fromEntries(columns.map(column => [columnLabel(column), row[columnLabel(column)]]))
  );

  const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(sortedData);

  ws['!cols'] = columns.map(column => ({
    wch: DEFAULT_COLUMN_WIDTHS[column] || 20
  }));

  ws['!rows'] = new Array(mappedData.length + 1).fill({ hpx: 25 });

  applyHoursDurationFormat(ws, columnLabel(ReportColumnsEnum.TOTAL), formatContext.formatDuration);

  if (columns.includes(ReportColumnsEnum.TASK_LINK)) {
    applyHyperlinksToColumn(ws, columnLabel(ReportColumnsEnum.TASK_LINK));
  }

  const workbook: XLSX.WorkBook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, ws, 'Dane');
  return workbook;
}

function applyHoursDurationFormat(
  ws: XLSX.WorkSheet,
  totalColumnName: string,
  formatDuration: TimeReportExcelFormatContext['formatDuration']
): void {
  let durationColumnIndex = '';

  Object.keys(ws).forEach(key => {
    if (key.startsWith('!') || typeof ws[key] !== 'object') return;
    if (ws[key].v === totalColumnName) {
      durationColumnIndex = key.charAt(0);
    }
  });

  Object.keys(ws).forEach(key => {
    if (key.startsWith('!') || typeof ws[key] !== 'object') return;

    if (durationColumnIndex && key.startsWith(durationColumnIndex)) {
      if (ws[key].v !== totalColumnName) {
        ws[key].v = formatDuration(String(ws[key].v ?? ''));
      }
      ws[key].t = 's';
    }
  });
}

function applyHyperlinksToColumn(ws: XLSX.WorkSheet, columnName: string): void {
  const columnLetter = findColumnLetterByHeader(ws, columnName);
  if (!columnLetter) {
    return;
  }

  for (let rowIndex = 2; rowIndex <= XLSX.utils.decode_range(ws['!ref'] ?? 'A1').e.r + 1; rowIndex++) {
    const cellKey = `${columnLetter}${rowIndex}`;
    const cell = ws[cellKey];
    if (!cell || typeof cell !== 'object' || typeof cell.v !== 'string' || !cell.v) {
      continue;
    }

    cell.l = { Target: cell.v };
  }
}

function findColumnLetterByHeader(ws: XLSX.WorkSheet, headerName: string): string {
  for (const key of Object.keys(ws)) {
    if (key.startsWith('!') || typeof ws[key] !== 'object') {
      continue;
    }

    if (ws[key].v === headerName) {
      return key.replace(/\d/g, '');
    }
  }

  return '';
}
